import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { datePropsToNumber, numberPropsToDate } from '../../utils/objectUtil'
import { getCategories } from '../common/category'
import { memberAddConfirmationChildUrl } from '../common/constant/appUrl'
import { selectMemberAddChildEntry, setMemberAddChild } from '../common/store/slices/memberAddChild'
import { getNow } from '../../utils/dateUtil'
import { useOperationLog } from '../common/operationLog'
import { OperationId } from '../common/constant/operationLog'
import { useErrorHandle } from '../common/error/errorHandler'
import { yesNo } from '../common/constant/classification'
import { showLoading } from '../common/store/slices/application'
import { GetCategoryMstDto } from '../../dataAccess/webApi/dto/categoryMstDto'

interface LocationState {
  /** 取得・入力済み情報から復元を試みる場合true */
  isKeep: boolean
  /**
   * 登録完了後遷移先。
   * 指定があれば登録完了後に指定画面へ遷移
   */
  completionTo?: Location
}

interface Inputs {
  name: string
  kana: string
  birthday: Date
  brothersSistersFlag: string
  multipleBirthsFlag: string
  gender: string
  bloodType: string
  maternalHandbookNo: string
}

export const useAction = () => {
  /** 使用する項目 */
  const CATEGORY_KEYS = [
    'childrenGender',
    'childrenBloodType',
    'maternalHandbookNo',
    'brothersSistersFlag',
    'multipleBirthsFlag',
  ]
  const DEFAULT_PROJECT_ID = '0' // 市民用の項目は事業ID'0'
  /** カテゴリーマスタをオブジェクト形式で保持する */
  const [categoryMap, setCategoryMap] = useState<{[key:string]: GetCategoryMstDto}>({})
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const currentHistory = useHistory<LocationState | undefined>()
  const history = useHistory()
  const { addOperationLog } = useOperationLog()

  const isKeep = !!currentHistory.location.state?.isKeep
  const completionTo = currentHistory.location.state?.completionTo

  const childBirthdayLimitDate = getNow() // 誕生日の入力上限値

  const addEntry = useSelector(selectMemberAddChildEntry)
  const formMethods = useForm<Inputs>({
    defaultValues: {
      ...(isKeep && addEntry && numberPropsToDate(addEntry, ['birthday'])),
    },
  })

  const load = useCallback(
    () => {
      dispatch(
        showLoading({
          process: errorHandle(async () => {
            const category = await getCategories({projectId: DEFAULT_PROJECT_ID, categories: CATEGORY_KEYS})
            const tmpMap: {[key:string]: GetCategoryMstDto} = {}
            category?.forEach(e => tmpMap[e.category] = e)
            setCategoryMap(tmpMap)
          }),
          isHiddenMain: true,
        })
      )
    },
    []
  )

  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001, accessData: [{ userIdRegFlag: yesNo.yes }] })
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = useCallback(
    (data: Inputs) => {
      addOperationLog({ operationId: OperationId.OP_00000060 })

      dispatch(setMemberAddChild(datePropsToNumber(data)))
      // 戻るで表示した際に取得・入力済み情報から復元を試みる為に履歴に保管
      currentHistory.replace({ ...currentHistory.location, state: { isKeep: true, completionTo } })
      history.push(memberAddConfirmationChildUrl.url(), { completionTo })
    },
    [dispatch, currentHistory, history, completionTo, addOperationLog]
  )

  return {
    formMethods,
    onSubmit,
    childBirthdayLimitDate,
    categoryMap
  }
}
